/**
 * ClinicalAssessment
 * PHQ-9 + GAD-7 validated clinical scales.
 * Shown post-onboarding and monthly as a follow-up.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import CrisisScreen from '@/components/CrisisScreen';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHQ9 = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself — or that you are a failure",
  "Trouble concentrating on things",
  "Moving or speaking unusually slowly or quickly",
  "Thoughts that you would be better off dead, or of hurting yourself",
];

const GAD7 = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid, as if something awful might happen",
];

const FREQ = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" },
];

function severity(score, type) {
  if (type === 'phq9') {
    if (score <= 4)  return { label: 'Minimal', color: '#4AACA8' };
    if (score <= 9)  return { label: 'Mild', color: '#B8906A' };
    if (score <= 14) return { label: 'Moderate', color: '#E07B4A' };
    if (score <= 19) return { label: 'Moderately Severe', color: '#C0392B' };
    return { label: 'Severe', color: '#8B0000' };
  }
  if (score <= 4)  return { label: 'Minimal', color: '#4AACA8' };
  if (score <= 9)  return { label: 'Mild', color: '#B8906A' };
  if (score <= 14) return { label: 'Moderate', color: '#E07B4A' };
  return { label: 'Severe', color: '#C0392B' };
}

export default function ClinicalAssessment() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const isFollowUp = searchParams.get('followup') === '1';

  const [user, setUser] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro | phq9 | gad7 | results | crisis
  const [phq9, setPhq9] = useState(Array(9).fill(null));
  const [gad7, setGad7] = useState(Array(7).fill(null));
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCrisis, setShowCrisis] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) { try { setUser(JSON.parse(stored)); } catch {} }
  }, []);

  const phq9Score = phq9.every(v => v !== null) ? phq9.reduce((a, b) => a + b, 0) : null;
  const gad7Score = gad7.every(v => v !== null) ? gad7.reduce((a, b) => a + b, 0) : null;

  const phq9Complete = phq9.every(v => v !== null);
  const gad7Complete = gad7.every(v => v !== null);

  const userId = user?.user_id || user?.sub || user?.email;

  const handleSave = async () => {
    if (!phq9Complete || !gad7Complete) { toast.error('Please answer all questions.'); return; }
    if (!userId) { toast.error('Session expired. Please reload and try again.'); return; }
    setSaving(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.post(`${API}/assessments`, {
        user_id: userId,
        phq9_answers: phq9,
        gad7_answers: gad7,
      }, cfg);
      setResults(res.data);
      // PHQ-9 item 9 > 0 = suicidal ideation flag
      if (res.data.crisis_flag || phq9[8] > 0) {
        setShowCrisis(true);
      } else {
        setPhase('results');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, #B8D3CF 100%)',
    padding: '40px 20px 100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const cardStyle = {
    background: 'rgba(255,255,255,0.9)',
    borderRadius: '20px',
    padding: '32px 28px',
    maxWidth: '560px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(30,107,104,0.1)',
  };

  // ── Crisis overlay ───────────────────────────────────────
  if (showCrisis) {
    return (
      <CrisisScreen
        onDismiss={() => { setShowCrisis(false); setPhase('results'); }}
      />
    );
  }

  // ── Shared back button ───────────────────────────────────
  const BackBtn = ({ label = '← Back', onClick }) => (
    <button
      onClick={onClick || (() => navigate(returnTo))}
      style={{ background: 'none', border: 'none', color: '#8FA3A0', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '16px', padding: 0, alignSelf: 'flex-start', display: 'block' }}
    >{label}</button>
  );

  // ── Intro ────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div style={containerStyle}>
        <div style={{ width: '100%', maxWidth: '560px' }}><BackBtn /></div>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🧭</div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", color: '#1E6B68', fontSize: '1.8rem', marginBottom: '12px' }}>
              {isFollowUp ? 'Monthly Check-In' : 'Baseline Assessment'}
            </h1>
            <p style={{ color: '#666', lineHeight: 1.7, fontSize: '0.95rem' }}>
              {isFollowUp
                ? "It's been a month since your last assessment. These 16 questions take about 3 minutes and help us track how you're progressing."
                : "Before you begin your HYPSEYA journey, we'd like to understand where you are right now. These 16 validated clinical questions take about 3 minutes."}
            </p>
          </div>

          <div style={{ background: 'rgba(30,107,104,0.06)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' }}>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#1E6B68', lineHeight: 1.6 }}>
              <strong>What this is:</strong> PHQ-9 (depression) + GAD-7 (anxiety) — two of the most widely used clinical screening tools in mental health. Your answers are private and used only to personalise your experience and track your progress over time.
            </p>
          </div>

          <p style={{ fontSize: '0.75rem', color: '#AAB8B6', textAlign: 'center', marginBottom: '24px', lineHeight: 1.5 }}>
            This is not a diagnosis. HYPSEYA is a wellness tool — not a medical device. If you are in crisis, please contact a professional.
          </p>

          <button
            onClick={() => setPhase('phq9')}
            style={{
              width: '100%', padding: '16px',
              background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              color: '#fff', border: 'none', borderRadius: '14px',
              fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
            }}
          >
            Begin assessment →
          </button>

          {!isFollowUp && (
            <button
              onClick={() => navigate(returnTo)}
              style={{ width: '100%', padding: '12px', background: 'transparent', border: 'none', color: '#AAB8B6', fontSize: '0.85rem', cursor: 'pointer', marginTop: '8px' }}
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── PHQ-9 ────────────────────────────────────────────────
  if (phase === 'phq9') {
    return (
      <div style={containerStyle}>
        <div style={{ width: '100%', maxWidth: '600px' }}><BackBtn onClick={() => setPhase('intro')} /></div>
        <div style={{ ...cardStyle, maxWidth: '600px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.1em', color: '#1E6B68', textTransform: 'uppercase' }}>PHQ-9 · Depression Screen</span>
              <span style={{ fontSize: '0.75rem', color: '#AAB8B6' }}>{phq9.filter(v => v !== null).length}/9</span>
            </div>
            <div style={{ height: '4px', background: '#E8F0EF', borderRadius: '2px' }}>
              <div style={{ height: '100%', width: `${(phq9.filter(v => v !== null).length / 9) * 100}%`, background: '#1E6B68', borderRadius: '2px', transition: 'width 0.3s' }} />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: '#666' }}>
              Over the <strong>last 2 weeks</strong>, how often have you been bothered by the following?
            </p>
          </div>

          {PHQ9.map((q, i) => (
            <div key={i} style={{
              marginBottom: '20px',
              padding: '16px',
              background: phq9[i] !== null ? 'rgba(30,107,104,0.04)' : 'rgba(255,255,255,0.6)',
              borderRadius: '12px',
              border: `1px solid ${phq9[i] !== null ? 'rgba(30,107,104,0.2)' : 'rgba(0,0,0,0.06)'}`,
              transition: 'all 0.2s',
            }}>
              <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#333', lineHeight: 1.5 }}>
                <span style={{ color: '#1E6B68', fontWeight: '700', marginRight: '8px' }}>{i + 1}.</span>
                {q}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {FREQ.map(f => (
                  <button
                    key={f.value}
                    onClick={() => {
                      const next = [...phq9];
                      next[i] = f.value;
                      setPhq9(next);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: phq9[i] === f.value ? '#1E6B68' : 'rgba(255,255,255,0.8)',
                      color: phq9[i] === f.value ? '#fff' : '#555',
                      border: `1.5px solid ${phq9[i] === f.value ? '#1E6B68' : '#E0E0E0'}`,
                      borderRadius: '8px',
                      fontSize: '0.78rem',
                      fontWeight: phq9[i] === f.value ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <button
            disabled={!phq9Complete}
            onClick={() => setPhase('gad7')}
            style={{
              width: '100%', padding: '16px',
              background: phq9Complete ? 'linear-gradient(135deg, #1E6B68, #2D8B87)' : '#E0E0E0',
              color: phq9Complete ? '#fff' : '#999',
              border: 'none', borderRadius: '14px',
              fontSize: '1rem', fontWeight: '600',
              cursor: phq9Complete ? 'pointer' : 'not-allowed',
              marginTop: '8px',
            }}
          >
            Continue to GAD-7 →
          </button>
        </div>
      </div>
    );
  }

  // ── GAD-7 ────────────────────────────────────────────────
  if (phase === 'gad7') {
    return (
      <div style={containerStyle}>
        <div style={{ width: '100%', maxWidth: '600px' }}><BackBtn onClick={() => setPhase('phq9')} /></div>
        <div style={{ ...cardStyle, maxWidth: '600px' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.1em', color: '#1E6B68', textTransform: 'uppercase' }}>GAD-7 · Anxiety Screen</span>
              <span style={{ fontSize: '0.75rem', color: '#AAB8B6' }}>{gad7.filter(v => v !== null).length}/7</span>
            </div>
            <div style={{ height: '4px', background: '#E8F0EF', borderRadius: '2px' }}>
              <div style={{ height: '100%', width: `${(gad7.filter(v => v !== null).length / 7) * 100}%`, background: '#2D8B87', borderRadius: '2px', transition: 'width 0.3s' }} />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: '#666' }}>
              Over the <strong>last 2 weeks</strong>, how often have you been bothered by the following?
            </p>
          </div>

          {GAD7.map((q, i) => (
            <div key={i} style={{
              marginBottom: '20px',
              padding: '16px',
              background: gad7[i] !== null ? 'rgba(30,107,104,0.04)' : 'rgba(255,255,255,0.6)',
              borderRadius: '12px',
              border: `1px solid ${gad7[i] !== null ? 'rgba(30,107,104,0.2)' : 'rgba(0,0,0,0.06)'}`,
              transition: 'all 0.2s',
            }}>
              <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#333', lineHeight: 1.5 }}>
                <span style={{ color: '#2D8B87', fontWeight: '700', marginRight: '8px' }}>{i + 1}.</span>
                {q}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {FREQ.map(f => (
                  <button
                    key={f.value}
                    onClick={() => {
                      const next = [...gad7];
                      next[i] = f.value;
                      setGad7(next);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: gad7[i] === f.value ? '#2D8B87' : 'rgba(255,255,255,0.8)',
                      color: gad7[i] === f.value ? '#fff' : '#555',
                      border: `1.5px solid ${gad7[i] === f.value ? '#2D8B87' : '#E0E0E0'}`,
                      borderRadius: '8px',
                      fontSize: '0.78rem',
                      fontWeight: gad7[i] === f.value ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <button
            disabled={!gad7Complete || saving}
            onClick={handleSave}
            style={{
              width: '100%', padding: '16px',
              background: gad7Complete ? 'linear-gradient(135deg, #1E6B68, #2D8B87)' : '#E0E0E0',
              color: gad7Complete ? '#fff' : '#999',
              border: 'none', borderRadius: '14px',
              fontSize: '1rem', fontWeight: '600',
              cursor: gad7Complete ? 'pointer' : 'not-allowed',
              marginTop: '8px',
            }}
          >
            {saving ? 'Saving...' : 'See my results →'}
          </button>

          <button
            onClick={() => setPhase('phq9')}
            style={{ width: '100%', padding: '10px', background: 'transparent', border: 'none', color: '#AAB8B6', fontSize: '0.85rem', cursor: 'pointer', marginTop: '4px' }}
          >
            ← Back to PHQ-9
          </button>
        </div>
      </div>
    );
  }

  // ── Results ──────────────────────────────────────────────
  if (phase === 'results' && results) {
    const phqSev = severity(results.phq9_score, 'phq9');
    const gadSev = severity(results.gad7_score, 'gad7');

    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✓</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#1E6B68', fontSize: '1.6rem', marginBottom: '8px' }}>
              Assessment complete
            </h2>
            <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.6 }}>
              This is a starting point — not a label. Your scores will be tracked over time so you can see real change.
            </p>
          </div>

          {/* PHQ-9 result */}
          <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `2px solid ${phqSev.color}22` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', color: '#666', textTransform: 'uppercase' }}>PHQ-9 · Depression</span>
              <span style={{ fontWeight: '700', color: phqSev.color, fontSize: '1.4rem' }}>{results.phq9_score}<span style={{ fontSize: '0.8rem', color: '#999' }}>/27</span></span>
            </div>
            <div style={{ height: '8px', background: '#F0F0F0', borderRadius: '4px', marginBottom: '10px' }}>
              <div style={{ height: '100%', width: `${(results.phq9_score / 27) * 100}%`, background: phqSev.color, borderRadius: '4px', transition: 'width 1s ease' }} />
            </div>
            <span style={{ display: 'inline-block', padding: '4px 12px', background: `${phqSev.color}18`, borderRadius: '20px', color: phqSev.color, fontSize: '0.82rem', fontWeight: '600' }}>
              {phqSev.label}
            </span>
          </div>

          {/* GAD-7 result */}
          <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '20px', marginBottom: '24px', border: `2px solid ${gadSev.color}22` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', color: '#666', textTransform: 'uppercase' }}>GAD-7 · Anxiety</span>
              <span style={{ fontWeight: '700', color: gadSev.color, fontSize: '1.4rem' }}>{results.gad7_score}<span style={{ fontSize: '0.8rem', color: '#999' }}>/21</span></span>
            </div>
            <div style={{ height: '8px', background: '#F0F0F0', borderRadius: '4px', marginBottom: '10px' }}>
              <div style={{ height: '100%', width: `${(results.gad7_score / 21) * 100}%`, background: gadSev.color, borderRadius: '4px', transition: 'width 1s ease' }} />
            </div>
            <span style={{ display: 'inline-block', padding: '4px 12px', background: `${gadSev.color}18`, borderRadius: '20px', color: gadSev.color, fontSize: '0.82rem', fontWeight: '600' }}>
              {gadSev.label}
            </span>
          </div>

          <div style={{ background: 'rgba(30,107,104,0.06)', borderRadius: '12px', padding: '14px 16px', marginBottom: '24px' }}>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#1E6B68', lineHeight: 1.6 }}>
              You'll be invited to retake this assessment in 30 days. Your progress will be tracked in <strong>Insights</strong> so you can see how your scores change over time.
            </p>
          </div>

          <button
            onClick={() => navigate(returnTo)}
            style={{
              width: '100%', padding: '16px',
              background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              color: '#fff', border: 'none', borderRadius: '14px',
              fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
            }}
          >
            {isFollowUp ? 'Back to Insights' : 'Begin my journey →'}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
